// Response schemas for the Leads area (plan §4 /leads/:type, spec §5.7, F11). Shapes follow
// apps/api/src/modules/leads/view.ts and kinds.ts; unknown keys are dropped. Only Leads roles
// (owner, supervisor, lead) ever receive these answers (constitution §6.4).
import { z } from 'zod';

// Caps: long Leads text is stored in TEXT columns (65,535 bytes), so any stored value parses;
// pack labels are up to 500 characters (review B3-R1: a tight cap broke whole pages). The caps
// only guard against an absurd answer.
const MAX_TEXT = 65_535;
const MAX_KEY = 64;
const MAX_STAMP = 64;
const MAX_LIST = 1000;
const MAX_PAGE_ITEMS = 100;

/** The record types with a table, as they appear in /leads/:type (capacity has its own call). */
export const LEADS_KINDS = [
  'risks',
  'acceptances',
  'asks',
  'decisions',
  'kpis',
  'gates',
  'questions',
] as const;
export type LeadsKind = (typeof LEADS_KINDS)[number];

export function isLeadsKind(value: string): value is LeadsKind {
  return (LEADS_KINDS as readonly string[]).includes(value);
}

export const ACCEPTANCE_STATUSES = ['to_sign', 'signed', 'declined'] as const;
export const ASK_STATUSES = ['pending', 'decided', 'deferred', 'declined'] as const;
export const ASK_DECISIONS = ['decided', 'deferred', 'declined'] as const;
export const ACCEPTANCE_DECISIONS = ['signed', 'declined'] as const;
export type AskDecision = (typeof ASK_DECISIONS)[number];
export type AcceptanceDecision = (typeof ACCEPTANCE_DECISIONS)[number];

const text = z.string().max(MAX_TEXT);
const optionalText = text.nullable();
// A calendar date as the DATE columns send it: "2026-11-04".
// Matches "2026-11-04"; rejects "4 Nov", "2026-11-04T00:00:00Z".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B4%7D-%5Cd%7B2%7D-%5Cd%7B2%7D%24&testString=2026-11-04%0A4%20Nov
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const stamp = z.string().max(MAX_STAMP);

const common = {
  key: z.string().min(1).max(MAX_KEY),
  source: z.string().max(MAX_KEY),
  version: z.number().int().nonnegative(),
  updatedAt: stamp,
  /** True while the pack holds values the owner has not reviewed yet. */
  packDiffers: z.boolean(),
};

export const riskSchema = z.object({
  ...common,
  risk: text,
  likelihood: optionalText,
  impact: optionalText,
  effect: optionalText,
  mitigation: optionalText,
  owner: optionalText,
  status: text,
});

export const acceptanceSchema = z.object({
  ...common,
  risk: text,
  cis: optionalText,
  compensatingControl: optionalText,
  accepter: optionalText,
  reviewOn: day.nullable(),
  /** The review date as the pack or the owner wrote it; may be free text ("after the readout"). */
  reviewOnText: optionalText,
  status: z.enum(ACCEPTANCE_STATUSES),
});

export const askSchema = z.object({
  ...common,
  decisionAsked: text,
  deadline: day.nullable(),
  deadlineText: optionalText,
  approver: optionalText,
  cost: optionalText,
  why: optionalText,
  tasks: z.array(z.string().max(MAX_KEY)).max(MAX_LIST).nullable(),
  status: z.enum(ASK_STATUSES),
  decidedOn: day.nullable(),
  decidedOnText: optionalText,
  /** The pack's decision history text; portal decisions are in `decisions` (review A3-R4). */
  decisionNote: optionalText,
});

export const decisionTakenSchema = z.object({
  ...common,
  topic: text,
  decision: text,
  by: optionalText,
});

export const kpiSchema = z.object({
  ...common,
  kpi: text,
  baseline: optionalText,
  target: optionalText,
  dataSource: optionalText,
  cadence: optionalText,
});

export const gateSchema = z.object({
  ...common,
  title: text,
  criteria: z.array(text).max(MAX_LIST),
  status: text,
});

export const openQuestionSchema = z.object({
  ...common,
  text,
  owner: optionalText,
  status: text,
});

export const leadsPersonSchema = z.object({
  id: z.number().int().positive(),
  name: text,
});

export const leadsDecisionSchema = z.object({
  decision: z.string().max(MAX_KEY),
  note: optionalText,
  by: leadsPersonSchema.nullable(),
  at: stamp,
});

export const kpiReadingSchema = z.object({
  id: z.number().int().positive(),
  readOn: day,
  value: text,
  note: optionalText,
  source: z.string().max(MAX_KEY),
  by: leadsPersonSchema.nullable(),
});

export const packReviewFieldSchema = z.object({
  field: z.string().min(1).max(MAX_KEY),
  portal: z.unknown(),
  pack: z.unknown(),
});

// Detail extras: the owner gets packReview; asks and acceptances carry their decisions, KPIs their
// readings. Every list is oldest first, as the API sends it.
const extras = {
  packReview: z.array(packReviewFieldSchema).max(MAX_LIST).optional(),
};

export const LEADS_ITEM_SCHEMAS = {
  risks: riskSchema,
  acceptances: acceptanceSchema,
  asks: askSchema,
  decisions: decisionTakenSchema,
  kpis: kpiSchema,
  gates: gateSchema,
  questions: openQuestionSchema,
} as const;

export const LEADS_DETAIL_SCHEMAS = {
  risks: riskSchema.extend(extras),
  acceptances: acceptanceSchema.extend({
    ...extras,
    decisions: z.array(leadsDecisionSchema).max(MAX_LIST),
  }),
  asks: askSchema.extend({ ...extras, decisions: z.array(leadsDecisionSchema).max(MAX_LIST) }),
  decisions: decisionTakenSchema.extend(extras),
  kpis: kpiSchema.extend({ ...extras, readings: z.array(kpiReadingSchema).max(MAX_LIST) }),
  gates: gateSchema.extend(extras),
  questions: openQuestionSchema.extend(extras),
} as const;

const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item).max(MAX_PAGE_ITEMS),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  });

export const LEADS_PAGE_SCHEMAS = {
  risks: pageOf(riskSchema),
  acceptances: pageOf(acceptanceSchema),
  asks: pageOf(askSchema),
  decisions: pageOf(decisionTakenSchema),
  kpis: pageOf(kpiSchema),
  gates: pageOf(gateSchema),
  questions: pageOf(openQuestionSchema),
} as const;

export type LeadsItemMap = { [K in LeadsKind]: z.infer<(typeof LEADS_ITEM_SCHEMAS)[K]> };
export type LeadsDetailMap = { [K in LeadsKind]: z.infer<(typeof LEADS_DETAIL_SCHEMAS)[K]> };
export interface LeadsPage<T> {
  readonly items: T[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
}
export type LeadsPageMap = { [K in LeadsKind]: LeadsPage<LeadsItemMap[K]> };
export type LeadsItem = LeadsItemMap[LeadsKind];
export type LeadsDetail = LeadsDetailMap[LeadsKind];

export type Risk = z.infer<typeof riskSchema>;
export type Acceptance = z.infer<typeof acceptanceSchema>;
export type Ask = z.infer<typeof askSchema>;
export type DecisionTaken = z.infer<typeof decisionTakenSchema>;
export type Kpi = z.infer<typeof kpiSchema>;
export type Gate = z.infer<typeof gateSchema>;
export type OpenQuestion = z.infer<typeof openQuestionSchema>;
export type LeadsPerson = z.infer<typeof leadsPersonSchema>;
export type LeadsDecision = z.infer<typeof leadsDecisionSchema>;
export type KpiReading = z.infer<typeof kpiReadingSchema>;
export type PackReviewField = z.infer<typeof packReviewFieldSchema>;
